import { html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LandiStorefrontElement } from '../base/landi-storefront-element.js';
import { sharedStyles, buttonStyles, layoutStyles, typographyStyles } from '../styles/shared.js';
import { biohacksRegistryStyles } from '../styles/biohacks-registry-styles.js';
import { storeApi, resolveProductImageUrl, type Product } from '@landi/store-core';
import './landi-product-card.js';

type PageItem = number | 'ellipsis';

function getVisiblePageNumbers(current: number, total: number): PageItem[] {
  if (total <= 0) return [];
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: PageItem[] = [1];
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);

  if (windowStart > 2) pages.push('ellipsis');
  for (let page = windowStart; page <= windowEnd; page++) pages.push(page);
  if (windowEnd < total - 1) pages.push('ellipsis');
  
  pages.push(total);
  return pages;
}

function formatProductLoadError(err: unknown): string {
  if (err instanceof Error) {
    if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
      const apiBase =
        typeof window !== 'undefined' ? window.LANDI_STORE_API_BASE?.trim() : '';
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      return apiBase
        ? `Could not reach store API (${apiBase}) from ${origin}. This is usually a CORS or network block — confirm this preview origin is allowed and the store API is deployed.`
        : 'Could not reach store API. Set window.LANDI_STORE_API_BASE in the page bootstrap.';
    }
    return err.message;
  }
  return 'Failed to load products';
}

/**
 * Product showcase grid with category filter and pagination
 * Styled to match The Golden Peptides services section
 * 
 * @element landi-product-showcase
 * @fires product-click - Fired when a product is clicked
 * @fires add-to-cart - Fired when add to cart is clicked
 */
@customElement('landi-product-showcase')
export class LandiProductShowcase extends LandiStorefrontElement {
  static styles = [
    sharedStyles,
    buttonStyles,
    layoutStyles,
    typographyStyles,
    biohacksRegistryStyles,
    css`
      :host {
        display: block;
        padding: 4rem 0;
        background: var(--landi-surface);
      }

      :host([layout='registry']) {
        padding: 0;
        background: transparent;
      }
      
      .showcase-container {
        max-width: 64rem;
        margin: 0 auto;
        padding: 0 1.5rem;
      }
      
      @media (min-width: 640px) {
        .showcase-container {
          padding: 0 3rem;
        }
      }
      
      .showcase-header {
        text-align: center;
        margin-bottom: 3rem;
        max-width: 40rem;
        margin-left: auto;
        margin-right: auto;
      }
      
      .category-filters {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 0.75rem;
        margin-bottom: 3rem;
      }
      
      .filter-btn {
        padding: 0.625rem 1.25rem;
        font-size: 0.75rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--landi-text-muted);
        background: transparent;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
        transition: all 200ms ease;
      }
      
      .filter-btn:hover {
        border-color: var(--landi-primary);
        color: var(--landi-primary);
      }
      
      .filter-btn.active {
        background: var(--landi-text);
        border-color: var(--landi-text);
        color: var(--landi-surface);
      }
      
      .products-grid {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        gap: 2rem;
        justify-content: center;
      }
      
      @media (min-width: 640px) {
        .products-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      
      @media (min-width: 1024px) {
        .products-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }
      
      .pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        margin-top: 3rem;
      }
      
      .page-btn {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--landi-text-muted);
        background: transparent;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
        transition: all 200ms ease;
      }
      
      .page-btn:hover:not(:disabled) {
        border-color: var(--landi-primary);
        color: var(--landi-primary);
      }
      
      .page-btn.active {
        background: var(--landi-text);
        border-color: var(--landi-text);
        color: var(--landi-surface);
      }
      
      .page-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .page-ellipsis {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        color: var(--landi-text-muted);
      }
      
      .view-all {
        display: flex;
        justify-content: center;
        margin-top: 3rem;
      }
      
      .empty-state {
        text-align: center;
        padding: 4rem 2rem;
        color: var(--landi-text-muted);
      }
      
      .empty-state h3 {
        font-family: var(--landi-font-serif);
        font-size: 1.5rem;
        color: var(--landi-text);
        margin-bottom: 0.5rem;
      }
    `,
  ];

  @property({ type: String }) title = 'Our Products';
  @property({ type: String }) subtitle = 'Browse our collection of high-quality research compounds.';
  @property({ type: String }) category = '';
  @property({ type: String }) tags = '';
  @property({ type: Boolean, attribute: 'in-stock-only' }) inStockOnly = true;
  @property({ type: Number, attribute: 'items-per-page' }) itemsPerPage = 6;
  @property({ type: Boolean, attribute: 'show-filters' }) showFilters = true;
  @property({ type: Boolean, attribute: 'hide-header' }) hideHeader = false;
  @property({ type: Boolean, attribute: 'show-pagination' }) showPagination = true;
  @property({ type: Boolean, attribute: 'featured-only' }) featuredOnly = false;
  @property({ type: String, attribute: 'view-all-href' }) viewAllHref = 'products.html';
  @property({ type: Boolean, attribute: 'show-view-all' }) showViewAll = false;
  @property({ type: String, attribute: 'product-href-base' }) productHrefBase = 'product.html?id=';
  @property({ type: String, attribute: 'search-query' }) searchQuery = '';
  /** v1.2 biohacks registry — catbar chips + 4-col prod grid matching reference HTML. */
  @property({ type: String }) layout = '';

  @state() private currentPage = 1;
  @state() private selectedCategory = '';
  @state() private categories: string[] = [];
  @state() private products: Product[] = [];
  @state() private loading = true;
  @state() private loadError = '';

  connectedCallback() {
    super.connectedCallback();
    this.selectedCategory = this.category;
    if (!this.searchQuery && typeof window !== 'undefined') {
      const urlQuery = new URLSearchParams(window.location.search).get('q')?.trim();
      if (urlQuery) {
        this.searchQuery = urlQuery;
      }
    }
    void this.loadProducts();
  }

  private get activeSearchQuery(): string {
    return this.searchQuery.trim();
  }

  private async loadProducts() {
    this.loading = true;
    this.loadError = '';
    try {
      if (this.featuredOnly) {
        this.products = await storeApi.getFeaturedProducts(this.itemsPerPage * 2);
      } else if (this.activeSearchQuery) {
        this.products = await storeApi.searchProducts(this.activeSearchQuery, this.selectedCategory || undefined);
      } else if (this.selectedCategory) {
        this.products = await storeApi.getProducts({ category: this.selectedCategory });
      } else {
        this.products = await storeApi.getProducts();
      }
      const cats = new Set<string>();
      this.products.forEach((p) => {
        if (p.category) cats.add(p.category);
      });
      this.categories = Array.from(cats);
    } catch (err) {
      this.loadError = formatProductLoadError(err);
      this.products = [];
    } finally {
      this.loading = false;
    }
  }

  private get filteredProducts(): Product[] {
    let filtered = this.products;
    
    if (this.inStockOnly) {
      filtered = filtered.filter(p => p.inventoryQuantity > 0);
    }
    
    if (this.tags) {
      const tagList = this.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
      if (tagList.length > 0) {
        filtered = filtered.filter(p => {
          const productTags = (p.tags || []).map(t => t.toLowerCase());
          return tagList.some(t => productTags.includes(t));
        });
      }
    }
    
    return filtered;
  }

  private get paginatedProducts(): Product[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredProducts.slice(start, start + this.itemsPerPage);
  }

  private get totalPages(): number {
    return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
  }

  private selectCategory(category: string) {
    this.selectedCategory = category;
    this.currentPage = 1;
    void this.loadProducts();
  }

  private goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  private get isRegistryLayout(): boolean {
    return this.layout === 'registry';
  }

  private resolveMolecularWeight(product: Product): string {
    const meta = product.metadata ?? {};
    const raw = meta.molecularWeight ?? meta.mw ?? meta.molecular_weight;
    return typeof raw === 'string' ? raw : '';
  }

  private resolveFill(product: Product): string {
    const meta = product.metadata ?? {};
    if (typeof meta.fill === 'string' && meta.fill) return meta.fill;
    const variant = product.variants?.[0];
    return variant?.name ?? '';
  }

  render() {
    const products = this.paginatedProducts;
    const registry = this.isRegistryLayout;

    if (this.loading) {
      return html`<section class="showcase-container"><p class="section-subtitle">Loading products…</p></section>`;
    }
    if (this.loadError) {
      return html`<section class="showcase-container"><p class="section-subtitle">${this.loadError}</p></section>`;
    }

    const filterNav = this.showFilters && (registry || this.categories.length > 1)
      ? html`
          <nav class="${registry ? 'catbar' : 'category-filters'}" aria-label="Product categories">
            <button
              type="button"
              class="${registry ? 'chip' : 'filter-btn'} ${!this.selectedCategory ? (registry ? 'sel' : 'active') : ''}"
              @click=${() => this.selectCategory('')}
            >
              All
            </button>
            ${this.categories.map(cat => html`
              <button
                type="button"
                class="${registry ? 'chip' : 'filter-btn'} ${this.selectedCategory === cat ? (registry ? 'sel' : 'active') : ''}"
                @click=${() => this.selectCategory(cat)}
              >
                ${cat}
              </button>
            `)}
          </nav>
        `
      : '';

    const productGrid = products.length > 0
      ? html`
          <div class="${registry ? 'grid' : 'products-grid'}">
            ${products.map(product => html`
              <landi-product-card
                layout=${registry ? 'registry' : ''}
                product-id=${product.id}
                .product=${product}
                name=${product.name}
                price=${product.price}
                .comparePrice=${product.compareAtPrice}
                image=${resolveProductImageUrl(product.images[0]?.url) || ''}
                sku=${product.sku || ''}
                category=${product.category || ''}
                description=${product.shortDescription || ''}
                rating=${product.metadata?.rating || 0}
                reviews=${product.metadata?.reviews || 0}
                .inStock=${product.inventoryQuantity > 0}
                .featured=${product.featured}
                format=${product.metadata?.format || ''}
                purity=${product.metadata?.purity || ''}
                test-method=${product.metadata?.testMethod || ''}
                quantity=${this.resolveFill(product)}
                molecular-weight=${this.resolveMolecularWeight(product)}
                href="${this.productHrefBase}${product.id}"
              ></landi-product-card>
            `)}
          </div>
        `
      : html`
          <div class="empty-state">
            <h3>No products found</h3>
            <p>Try adjusting your filters or check back later.</p>
          </div>
        `;

    return html`
      <section class="showcase-container">
        ${this.hideHeader ? nothing : html`
          <header class="showcase-header">
            <span class="section-label">What We Offer</span>
            <h2 class="section-title">${this.title}</h2>
            <p class="section-subtitle">${this.subtitle}</p>
          </header>
        `}

        ${filterNav}
        ${productGrid}

        ${!registry && this.showPagination && this.totalPages > 1 ? html`
            <nav class="pagination" aria-label="Pagination">
              <button 
                class="page-btn"
                ?disabled=${this.currentPage === 1}
                @click=${() => this.goToPage(this.currentPage - 1)}
                aria-label="Previous page"
              >
                ←
              </button>
              
              ${getVisiblePageNumbers(this.currentPage, this.totalPages).map(page => page === 'ellipsis' ? html`
                <span class="page-ellipsis" aria-hidden="true">…</span>
              ` : html`
                <button 
                  class="page-btn ${this.currentPage === page ? 'active' : ''}"
                  @click=${() => this.goToPage(page as number)}
                  aria-label="Page ${page}"
                  aria-current=${this.currentPage === page ? 'page' : 'false'}
                >
                  ${page}
                </button>
              `)}
              
              <button 
                class="page-btn"
                ?disabled=${this.currentPage === this.totalPages}
                @click=${() => this.goToPage(this.currentPage + 1)}
                aria-label="Next page"
              >
                →
              </button>
            </nav>
          ` : ''}

        ${!registry && this.showViewAll ? html`
          <div class="view-all">
            <a href=${this.viewAllHref} class="btn btn-secondary btn-lg">
              View Full Catalog
            </a>
          </div>
        ` : ''}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-product-showcase': LandiProductShowcase;
  }
}
